import Cookies from 'js-cookie'
import { useState } from 'react'
import { useRouter } from 'next/router'
import useIsSsr from 'hooks/useIsSsr'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { getBannerCopy, getQuizOutcomeContent, quizQuestions } from './helpers'

import { FEATURE_TOGGLES } from 'constants/featureToggles'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'
import { createExperimentObject } from 'utils/experiments'

import { QuizBanner, QuizFlowSplash, QuizFlowQuestion, QuizFlowResult } from '.'

const QuizFlow = function ({ associatedProduct }) {
  // PFQ cookie for styling and component rendering
  const PRODUCT_FIT_QUIZ_COMPLETE_COOKIE = 'product_fit_quiz_completed'
  const productFitQuizCookie = Cookies.get(PRODUCT_FIT_QUIZ_COMPLETE_COOKIE)
  const [showQuizBannerState, setShowQuizBannerState] = useState(!productFitQuizCookie)
  const [showQuizState, setShowQuizState] = useState(false)
  const [showQuizQuestions, setShowQuizQuestions] = useState(false)
  const [showQuizResult, setShowQuizResult] = useState(false)
  const [quizResultContent, setQuizResultContent] = useState()

  const isSsr = useIsSsr()
  const router = useRouter()
  const { fireAndForget } = useTriggerEvents()

  const visitId = Cookies.get('eh_visit_id')

  // for fire and forget events
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const PFQBannerExperiment = useFeatureFlag(
    FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER
  )

  const setQuizComplete = () => {
    fireAndForget({
      event_type: 'app_event',
      event_action: 'app_event',
      nav_link_section: 'PFQ Flow',
      track_event: 'product_fit_quiz_flow_complete',
      experiments: [
        createExperimentObject(
          'Product Fit Quiz Banner',
          FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
          PFQBannerExperiment,
          'conversion'
        ),
      ],
    })

    Cookies.set(PRODUCT_FIT_QUIZ_COMPLETE_COOKIE, true, {
      expires: 90,
    })

    setShowQuizBannerState(false)
  }

  const startQuizHandler = () => {
    fireAndForget({
      event_type: 'button_click',
      event_action: 'button_click',
      nav_link_section: 'PFQ Banner',
      track_event: 'product_fit_quiz_banner_start',
      experiments: [
        createExperimentObject(
          'Product Fit Quiz Banner',
          FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
          PFQBannerExperiment,
          'conversion'
        ),
      ],
    })

    setShowQuizState(true)
  }

  const declineQuizHandler = () => {
    fireAndForget({
      event_type: 'button_click',
      event_action: 'button_click',
      nav_link_section: 'PFQ Banner',
      track_event: 'product_fit_quiz_banner_decline',
      experiments: [
        createExperimentObject(
          'Product Fit Quiz Banner',
          FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
          PFQBannerExperiment,
          'conversion'
        ),
      ],
    })

    Cookies.set(PRODUCT_FIT_QUIZ_COMPLETE_COOKIE, true, {
      expires: 1, // just 1 day when they click no to the quiz
    })

    setShowQuizBannerState(false)
  }

  const startQuestionsHandler = () => {
    setShowQuizState(false)
    setShowQuizQuestions(true)
  }

  const closeSplashHandler = () => {
    setShowQuizState(false)
  }

  const closeQuestionsHandler = () => {
    setShowQuizQuestions(false)
  }

  const completeQuizHandler = ({ answers }) => {
    const newQuizResultContent = getQuizOutcomeContent({ answers })

    fireAndForget({
      event_type: 'app_event',
      event_action: 'app_event',
      nav_link_section: 'PFQ Questions',
      track_event: 'product_fit_quiz_questions_complete',
      pfq_answers: answers,
      pfq_result: newQuizResultContent,
      experiments: [
        createExperimentObject(
          'Product Fit Quiz Banner',
          FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
          PFQBannerExperiment,
          'conversion'
        ),
      ],
    })

    setQuizResultContent(newQuizResultContent)
    setShowQuizQuestions(false)

    if (newQuizResultContent) {
      // failsafe
      setShowQuizResult(true)
    }
  }

  const closeResultHandler = () => {
    setQuizComplete()
    setShowQuizResult(false)
  }

  const resultCTAHandler = ({ flowRedirectPath }) => {
    setQuizComplete()
    setShowQuizResult(false)

    if (flowRedirectPath) {
      const queryParams = new URLSearchParams(router.query)
      // attach the Visit ID
      if (visitId) queryParams.set('visit_id', visitId)
      const redirectUrl = `${flowRedirectPath}${queryParams ? `&${queryParams.toString()}` : ''}`
      router.push(redirectUrl)
    }
  }

  return (
    <>
      {!isSsr && showQuizBannerState && (
        <QuizBanner
          showQuizBanner={showQuizBannerState}
          bannerCopy={getBannerCopy(associatedProduct)}
          onStart={startQuizHandler}
          onDecline={declineQuizHandler}
        />
      )}
      {showQuizState && (
        <QuizFlowSplash onStart={startQuestionsHandler} onClose={closeSplashHandler} />
      )}
      {showQuizQuestions && (
        <QuizFlowQuestion
          quizQuestions={quizQuestions}
          onComplete={completeQuizHandler}
          onClose={closeQuestionsHandler}
        />
      )}
      {showQuizResult && (
        <QuizFlowResult
          content={quizResultContent}
          onClose={closeResultHandler}
          onButtonClick={resultCTAHandler}
        />
      )}
    </>
  )
}

export { QuizFlow }
